import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useTerminologyExport, useTerminologyStarterPack } from '../hooks/useTerminologyExport';
import type { ApiError, TerminologyExportFormat, TerminologyExportType } from '../types/terminology';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface TerminologyDownloadMenuProps {
  /** 표준용어/금칙어 리스트 화면은 유형이 고정. 대시보드는 undefined 로 두어 선택 가능하게 함 */
  fixedType?: TerminologyExportType;
  /** 현재 리스트 화면의 source 필터 값 — 체크 시 export 의 `group` 파라미터로 전달 */
  currentGroup?: string;
  /** 현재 리스트 화면의 "폐기 포함" 체크 여부 — 체크 시 export 의 `status=ALL` 로 전달 */
  currentIncludeDeprecated?: boolean;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
}

export function TerminologyDownloadMenu({
  fixedType,
  currentGroup,
  currentIncludeDeprecated,
  onError,
  onSuccess,
}: TerminologyDownloadMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState<TerminologyExportFormat>('json');
  const [type, setType] = useState<TerminologyExportType>(fixedType ?? 'standard');
  const hasFilter = !!(currentGroup || currentIncludeDeprecated);
  const [applyFilter, setApplyFilter] = useState(hasFilter);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const exportMutation = useTerminologyExport();
  const starterPackMutation = useTerminologyStarterPack();

  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler, { capture: true });
    return () => document.removeEventListener('keydown', handler, { capture: true });
  }, [open]);

  const handleDownload = useCallback(() => {
    exportMutation.mutate(
      {
        format,
        type,
        group: applyFilter ? currentGroup || undefined : undefined,
        status: applyFilter && currentIncludeDeprecated ? 'ALL' : undefined,
      },
      {
        onSuccess: ({ blob, filename }) => {
          downloadBlob(blob, filename);
          setOpen(false);
          onSuccess?.(t('terminologyManagement.export.toast.success'));
        },
        onError: (err) => {
          onError?.((err as ApiError)?.message ?? t('terminologyManagement.error.network'));
        },
      }
    );
  }, [exportMutation, format, type, applyFilter, currentGroup, currentIncludeDeprecated, onSuccess, onError, t]);

  const handleStarterPack = useCallback(() => {
    starterPackMutation.mutate(undefined, {
      onSuccess: ({ blob, filename }) => {
        downloadBlob(blob, filename);
        onSuccess?.(t('terminologyManagement.export.toast.starterPackSuccess'));
      },
      onError: (err) => {
        onError?.((err as ApiError)?.message ?? t('terminologyManagement.error.network'));
      },
    });
  }, [starterPackMutation, onSuccess, onError, t]);

  const btnStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    fontFamily: 'var(--font-family-sans)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'flex', gap: 'var(--spacing-2)' }}>
      <button
        type="button"
        data-testid="terminology-download-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        style={btnStyle}
      >
        {t('terminologyManagement.export.button.download')}
      </button>

      <button
        type="button"
        data-testid="terminology-starterpack-btn"
        onClick={handleStarterPack}
        disabled={starterPackMutation.isPending}
        aria-busy={starterPackMutation.isPending}
        style={{ ...btnStyle, opacity: starterPackMutation.isPending ? 0.6 : 1 }}
      >
        {starterPackMutation.isPending
          ? t('terminologyManagement.export.button.starterPackLoading')
          : t('terminologyManagement.export.button.starterPack')}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={t('terminologyManagement.export.menu.label')}
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--spacing-2))',
            right: 0,
            minWidth: 240,
            background: 'var(--color-surface-background)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            zIndex: 200,
            padding: 'var(--spacing-4)',
          }}
        >
          <div style={{ marginBottom: 'var(--spacing-3)' }}>
            <label
              htmlFor={`${menuId}-format`}
              style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}
            >
              {t('terminologyManagement.export.field.format')}
            </label>
            <select
              id={`${menuId}-format`}
              data-testid="terminology-download-format"
              value={format}
              onChange={(e) => setFormat(e.target.value as TerminologyExportFormat)}
              style={selectStyle}
            >
              <option value="json">JSON</option>
              <option value="csv">CSV</option>
              <option value="xlsx">XLSX</option>
            </select>
          </div>

          {!fixedType && (
            <div style={{ marginBottom: 'var(--spacing-3)' }}>
              <label
                htmlFor={`${menuId}-type`}
                style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}
              >
                {t('terminologyManagement.export.field.type')}
              </label>
              <select
                id={`${menuId}-type`}
                data-testid="terminology-download-type"
                value={type}
                onChange={(e) => setType(e.target.value as TerminologyExportType)}
                style={selectStyle}
              >
                <option value="standard">{t('terminologyManagement.term.list.title')}</option>
                <option value="forbidden">{t('terminologyManagement.forbidden.list.title')}</option>
              </select>
            </div>
          )}

          {hasFilter && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-1)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-3)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                data-testid="terminology-download-scope"
                checked={applyFilter}
                onChange={(e) => setApplyFilter(e.target.checked)}
              />
              {t('terminologyManagement.export.field.applyFilter')}
            </label>
          )}

          <button
            type="button"
            data-testid="terminology-download-submit"
            onClick={handleDownload}
            disabled={exportMutation.isPending}
            aria-busy={exportMutation.isPending}
            style={{
              width: '100%',
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: exportMutation.isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: exportMutation.isPending ? 0.7 : 1,
            }}
          >
            {exportMutation.isPending
              ? t('terminologyManagement.export.button.downloading')
              : t('terminologyManagement.export.button.downloadSubmit')}
          </button>
        </div>
      )}
    </div>
  );
}
